<template>
  <!--
    ArtifactDetail SECTION — Metric artifact, "Performance Domain" tab.

    WHAT: Manages which performance domains this metric belongs to (controls where
      it appears in the /insight sidebar). UI is removable chips + an "add to
      domain…" dropdown of non-member domains, NOT a checkbox grid.

    CONTRACT (props { record, artifact, context }):
      READS  context.domains (all domains for the tenant), context.domainsByMetric
             [record.id] (seeds the local memberIds set).
      DOES NOT emit `patch` — it is an endpoint-calling exception to the section
             contract: each toggle POSTs
             /api/performancedomains/{domain.id}/assign_metrics/ with
             { add_metric_ids:[record.id] } or { remove_metric_ids:[record.id] },
             then emits refresh() so the shell reloads shared context.

    NOTES:
      - Saves immediately (no debounce); shows an inline saveMsg / saveErr toast.
      - Local memberIds Set is reassigned after each change to force reactivity.
      - NOT selfChrome — rendered inside ArtifactDetail's standard titled card.
  -->
  <div>
    <div v-if="!domains.length" class="text-xs text-gray-400">No performance domains for this tenant yet.</div>
    <template v-else>
      <div class="flex flex-wrap items-center gap-2">
        <!-- current memberships as removable chips -->
        <span
          v-for="d in memberDomains"
          :key="d.id"
          class="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border bg-blue-50 border-blue-200 text-blue-800"
        >
          {{ d.name }}
          <button class="text-blue-400 hover:text-red-500 leading-none" :disabled="busy" title="Remove" @click="toggle(d)">×</button>
        </span>
        <span v-if="!memberDomains.length" class="text-xs text-gray-400">Not in any domain.</span>

        <!-- add dropdown (only non-member domains) -->
        <select
          v-if="availableDomains.length"
          class="text-xs px-2 py-1 border border-dashed border-gray-300 rounded-lg text-gray-500 bg-transparent focus:outline-none focus:border-blue-400"
          :disabled="busy"
          @change="onAdd($event)"
        >
          <option value="">＋ add to domain…</option>
          <option v-for="d in availableDomains" :key="d.id" :value="d.id">{{ d.name }}</option>
        </select>
      </div>
      <p v-if="saveMsg" class="text-[11px] mt-2" :class="saveErr ? 'text-red-500' : 'text-emerald-600'">{{ saveMsg }}</p>
      <p class="text-[10px] text-gray-400 mt-2">Controls where this metric appears in the /insight sidebar. Saved immediately.</p>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DomainSection',
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch', 'refresh'],
  data() {
    return {
      busy: false,
      saveMsg: '',
      saveErr: false,
      // optimistic per-domain overrides applied on top of live server data
      overrides: {},
      // fresh copy fetched on mount + after every toggle — the shell's context
      // loads reference data ONCE per session and never refreshes it.
      freshDomains: null,
    };
  },
  computed: {
    domains() { return this.freshDomains || this.context.domains || []; },
    // Membership computed LIVE from the domain serializer's own link lists —
    // never from a snapshot taken at mount (the old seeding went stale after
    // the first toggle and made every later add look like a failure).
    memberIds() {
      const s = new Set();
      for (const d of this.domains) {
        const links = d.composable_metrics || [];
        if (links.some((cm) => cm.metric_id === this.record.id)) s.add(d.id);
      }
      for (const [id, on] of Object.entries(this.overrides)) {
        if (on) s.add(Number(id)); else s.delete(Number(id));
      }
      return s;
    },
    memberDomains() { return this.domains.filter((d) => this.memberIds.has(d.id)); },
    availableDomains() { return this.domains.filter((d) => !this.memberIds.has(d.id)); },
  },
  mounted() { this.reloadDomains(); },
  methods: {
    async reloadDomains() {
      try {
        const { data } = await axios.get('/api/performancedomains/');
        this.freshDomains = Array.isArray(data) ? data : (data.results || []);
        this.overrides = {};
      } catch (e) { /* fall back to context snapshot */ }
    },
    isMember(id) { return this.memberIds.has(id); },
    onAdd(e) {
      const id = Number(e.target.value);
      e.target.value = '';
      const domain = this.domains.find((d) => d.id === id);
      if (domain) this.toggle(domain);
    },
    async toggle(domain) {
      if (this.busy) return;
      this.busy = true;
      this.saveMsg = '';
      const adding = !this.memberIds.has(domain.id);
      try {
        const body = adding ? { add_metric_ids: [this.record.id] } : { remove_metric_ids: [this.record.id] };
        const { data } = await axios.post(`/api/performancedomains/${domain.id}/assign_metrics/`, body);
        // HTTP 200 is not success — the server reports how many rows it
        // actually touched. A remove that deleted nothing must NOT flip the
        // chip and pretend it worked. (An add of an existing member is a
        // benign no-op — the chip state is already the truth.)
        if (!adding && !data?.removed) {
          this.saveErr = true;
          this.saveMsg = `Server removed nothing from ${domain.name} — membership unchanged. Check the link row.`;
          return;
        }
        this.overrides = { ...this.overrides, [domain.id]: adding };
        this.saveErr = false;
        this.saveMsg = adding ? `Added to ${domain.name}` : `Removed from ${domain.name}`;
        this.$emit('refresh');
        this.reloadDomains();
        setTimeout(() => { this.saveMsg = ''; }, 2500);
      } catch (e) {
        this.saveErr = true;
        this.saveMsg = `Failed: ${e?.response?.data?.detail || e.message}`;
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
